/* eslint-disable */
// @ts-nocheck
import "react-datepicker/dist/react-datepicker.min.css";
import { createRoot } from "react-dom/client";

import { initRouter } from "@/utils/router";
import { route as routeFn } from "@/utils/router";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { createInertiaApp } from "@inertiajs/react";
import { router as inertiaRouter } from "@inertiajs/react";
import AppProvider from "@providers/AppProvider";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import * as process from "process";

import "./i18n";
// DO NOT REMOVE - linked to static & chargebee image.
import "./imports/imageImports";

const appName =
    process.env &&
    process.env.APP_NAME !== undefined &&
    process.env.APP_NAME !== null
        ? process.env.APP_NAME
        : "Homeschooling Australia - Home School Programs | Euka";

/**
 * Non-breaking error patterns to suppress from Datadog error tracking.
 * These are known noise that do not impact user flow:
 *  - Script error / Empty reason (cross-origin or browser-internal noise)
 *  - ResizeObserver loop (benign browser warning when callback exceeds one frame)
 *  - Third-party tracking pixel errors (bat.bing, GA, GTM, doubleclick)
 */
const SUPPRESSED_ERROR_PATTERNS = [
    "Script error.",
    "Empty reason",
    "ResizeObserver loop",
    "bat.bing.com",
    "google-analytics.com",
    "googletagmanager.com",
    "doubleclick.net"
];

const isNonBreakingError = (message: string | undefined): boolean => {
    if (!message) return false;
    return SUPPRESSED_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

if (
    process.env &&
    process.env.DATADOG_APPLICATION_ID !== undefined &&
    process.env.DATADOG_APPLICATION_ID !== null
) {
    try {
        datadogRum.init({
            applicationId: process.env.DATADOG_APPLICATION_ID,
            clientToken: process.env.DATADOG_CLIENT_TOKEN,
            site: "datadoghq.com",
            service: `portal-ui-${process.env.PIPELINE_ENVIRONMENT}`,
            env: process.env.PIPELINE_ENVIRONMENT,
            // Specify a version number to identify the deployed version of your application in Datadog.
            version: process.env.UI_VERSION,
            sessionSampleRate:
                parseInt(process.env.DATADOG_SESSION_SAMPLE_RATE) || 100,
            sessionReplaySampleRate:
                parseInt(process.env.DATADOG_SESSION_REPLAY_SAMPLE_RATE) || 100,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: "mask-user-input",
            allowedTracingUrls: [process.env.APP_URL],
            beforeSend: event => {
                if (event.type === "error") {
                    const msg = event.error?.message ?? "";
                    if (isNonBreakingError(msg)) return false;
                }
                return true;
            }
        });

        datadogLogs.init({
            clientToken: process.env.DATADOG_CLIENT_TOKEN,
            site: "datadoghq.com",
            service: `portal-ui-${process.env.PIPELINE_ENVIRONMENT}`,
            env: process.env.PIPELINE_ENVIRONMENT,
            version: process.env.UI_VERSION,
            forwardErrorsToLogs: true,
            forwardConsoleLogs: ["warn", "error"],
            forwardReports: "all",
            sessionSampleRate:
                parseInt(process.env.DATADOG_SESSION_SAMPLE_RATE) || 100
        });

        datadogLogs.logger.info("Initialised Datadog logger successfully");
    } catch (exception) {
        console.log("DD Failed!", { error: exception });
    }
}

createInertiaApp({
    title: title => `${title} - ${appName}`,
    resolve: name =>
        resolvePageComponent(
            `./inertia-pages/${name}.tsx`,
            import.meta.glob("./inertia-pages/**/*.tsx")
        ),
    setup({ el, App, props }) {
        const root = createRoot(el);

        // 1) Initialise from the first payload (no hooks needed)
        const { routeManifest, baseUrl } =
            (props.initialPage.props as any) ?? {};
        initRouter(
            routeManifest ?? {},
            (baseUrl ?? window.location.origin).replace(/\/+$/, "")
        );

        // 2) Set up global route function with error handling that returns fallback instead of throwing
        (globalThis as any).route = (
            name: unknown,
            params?: unknown,
            absolute?: unknown
        ) => {
            if (typeof name !== "string" || !name) {
                return "/";
            }

            try {
                return routeFn(name as string, params as any, absolute as any);
            } catch (error) {
                // Return home page instead of throwing an error
                return "/";
            }
        };

        // 3) Refresh on client-side navigations
        const handleNavigate = (event: any) => {
            const p = event?.detail?.page?.props as any;
            initRouter(
                p?.routeManifest ?? {},
                (p?.baseUrl ?? window.location.origin).replace(/\/+$/, "")
            );
        };

        if (typeof inertiaRouter?.on === "function") {
            inertiaRouter.on("navigate", handleNavigate);
        } else {
            // Fallback for older Inertia versions that use DOM events
            window.addEventListener(
                "inertia:navigate",
                handleNavigate as EventListener
            );
        }

        const renderAllApps = () => {
            return (
                <AppProvider>
                    <App {...props} />
                </AppProvider>
            );
        };

        root.render(renderAllApps());
    },
    progress: {
        color: "#f0a32f" // theme["colors"]["burntYellow"][500]
    }
});
